import { useCallback } from 'react';

import { useNoticeBannersStackContext } from '@sg/garnish';

import { type NotificationCategories } from '../../../hooks';
import { useSetUserNotificationPreferencesMutation } from '../graphql/SetUserNotificationPreferences.generated';

// ─────────────────────────────────────────────────────────────────────

export const useNotificationPreferencesUpdates = (params: Params) => {
  const { failureMessage } = params;

  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  const [{ fetching }, updateNotificationPreferences] = useSetUserNotificationPreferencesMutation();

  // ─────────────────────────────────────────────────────────────────────

  const setNotificationPreferences = useCallback(
    async (payload: NotificationCategories) => {
      const updatedPreferences = {
        input: {
          notificationCategories: payload.map((preference) => ({
            id: preference.id,
            emailSubscribe: preference.emailSubscribe,
            pushSubscribe: preference.pushSubscribe,
          })),
        },
      };

      const result = await updateNotificationPreferences(updatedPreferences);
      const data = result.data?.setUserNotificationPreferences;

      const hasUpdated = data?.__typename === 'UserNotificationPreferences';
      const hasError = result.error || !hasUpdated;

      if (hasError) {
        addNoticeBanner({ text: failureMessage, palette: 'caution' });

        return false;
      }

      return true;
    },
    [addNoticeBanner, failureMessage, updateNotificationPreferences],
  );

  // ─────────────────────────────────────────────────────────────────────

  return { setNotificationPreferences, isUpdating: fetching };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  failureMessage: string;
};
